import React, { useEffect, useState } from 'react';
import {
  Alert, Button, Checkbox, CircularProgress, Dialog, DialogActions, DialogContent, DialogContentText, DialogTitle,
  FormControl, FormControlLabel, InputLabel, List, ListItem, ListItemText, MenuItem, Select, Stack, TextField, Typography,
} from '@mui/material';
import apiService from '../../services/api.service';
import { SoaAppliesTo, SoaTemplateRecord, SoaTemplateSummary, SoaTemplateVersion } from '../../types/formattedSoa';
import { soaErrorMessage } from '../../utils/formattedSoa';

export const APPLIES_LABELS: Record<SoaAppliesTo, string> = { ALL: 'Customers and suppliers', C: 'Customers', S: 'Suppliers' };

// ── confirm ──────────────────────────────────────────────────────────────────────────────────

export const ConfirmDialog: React.FC<{
  open: boolean; title: string; text: React.ReactNode; confirmLabel: string; danger?: boolean; busy?: boolean;
  onConfirm: () => void; onClose: () => void;
}> = ({ open, title, text, confirmLabel, danger, busy, onConfirm, onClose }) => (
  <Dialog open={open} onClose={busy ? undefined : onClose} maxWidth="xs" fullWidth>
    <DialogTitle>{title}</DialogTitle>
    <DialogContent><DialogContentText component="div">{text}</DialogContentText></DialogContent>
    <DialogActions>
      <Button onClick={onClose} disabled={busy}>Cancel</Button>
      <Button variant="contained" color={danger ? 'error' : 'primary'} onClick={onConfirm} disabled={busy}
        startIcon={busy ? <CircularProgress size={14} /> : undefined}>
        {confirmLabel}
      </Button>
    </DialogActions>
  </Dialog>
);

// ── new template ─────────────────────────────────────────────────────────────────────────────

export const NewTemplateDialog: React.FC<{
  open: boolean; companyId: number; templates: SoaTemplateSummary[]; onClose: () => void; onCreated: (templateId: number) => void;
}> = ({ open, companyId, templates, onClose, onCreated }) => {
  const [name, setName] = useState('');
  const [description, setDescription] = useState('');
  const [appliesTo, setAppliesTo] = useState<SoaAppliesTo>('ALL');
  const [start, setStart] = useState('standard');
  const [busy, setBusy] = useState(false);
  const [error, setError] = useState<string | null>(null);

  useEffect(() => {
    if (open) { setName(''); setDescription(''); setAppliesTo('ALL'); setStart('standard'); setError(null); }
  }, [open]);

  const create = async () => {
    setBusy(true);
    setError(null);
    try {
      let res: any;
      if (start.startsWith('t:')) {
        const source: any = await apiService.getSoaTemplate(Number(start.slice(2)));
        res = await apiService.createSoaTemplate({ companyId, name: name.trim(), description, appliesTo, definition: source?.data?.definition });
      } else {
        res = await apiService.createSoaTemplate({ companyId, name: name.trim(), description, appliesTo, from: start as 'standard' | 'blank' });
      }
      onCreated(Number(res?.data?.templateId));
    } catch (e: any) {
      setError(soaErrorMessage(e, 'The template could not be created'));
    } finally {
      setBusy(false);
    }
  };

  return (
    <Dialog open={open} onClose={busy ? undefined : onClose} maxWidth="sm" fullWidth>
      <DialogTitle>New statement template</DialogTitle>
      <DialogContent>
        <Stack spacing={2} sx={{ mt: 1 }}>
          {error && <Alert severity="error">{error}</Alert>}
          <TextField label="Name" value={name} onChange={(e) => setName(e.target.value)} inputProps={{ maxLength: 120 }} autoFocus required />
          <TextField label="Description" value={description} onChange={(e) => setDescription(e.target.value)} inputProps={{ maxLength: 500 }} multiline minRows={2} />
          <FormControl>
            <InputLabel>For</InputLabel>
            <Select label="For" value={appliesTo} onChange={(e) => setAppliesTo(e.target.value as SoaAppliesTo)}>
              {(Object.keys(APPLIES_LABELS) as SoaAppliesTo[]).map((k) => <MenuItem key={k} value={k}>{APPLIES_LABELS[k]}</MenuItem>)}
            </Select>
          </FormControl>
          <FormControl>
            <InputLabel>Start from</InputLabel>
            <Select label="Start from" value={start} onChange={(e) => setStart(String(e.target.value))}>
              <MenuItem value="standard">Standard layout (logo, account box, running balance)</MenuItem>
              <MenuItem value="blank">Blank page with the transaction table</MenuItem>
              {templates.map((t) => <MenuItem key={t.templateId} value={`t:${t.templateId}`}>A copy of “{t.name}”</MenuItem>)}
            </Select>
          </FormControl>
        </Stack>
      </DialogContent>
      <DialogActions>
        <Button onClick={onClose} disabled={busy}>Cancel</Button>
        <Button variant="contained" onClick={create} disabled={busy || !name.trim()} startIcon={busy ? <CircularProgress size={14} /> : undefined}>
          Create and open
        </Button>
      </DialogActions>
    </Dialog>
  );
};

// ── copy to companies ────────────────────────────────────────────────────────────────────────

interface CompanyOption { id: number; name: string; code: string }

async function loadMyCompanies(): Promise<CompanyOption[]> {
  const map = (raw: any[]): CompanyOption[] => raw
    .map((c: any) => ({ id: Number(c.CompanyID ?? c.companyid), name: String(c.CompanyName ?? c.companyname ?? ''), code: String(c.CompanyCode ?? c.companycode ?? '') }))
    .filter((c) => c.id > 0);
  try {
    const res: any = await apiService.getMyCompanies(true);
    const list = map(Array.isArray(res) ? res : res?.data || []);
    if (list.length) return list;
  } catch {
    /* fall back to every company */
  }
  const all: any = await apiService.getCompanies(true);
  return map(Array.isArray(all) ? all : all?.data || []);
}

export const CopyTemplateDialog: React.FC<{
  open: boolean; template: SoaTemplateSummary | null; currentCompanyId: number; onClose: () => void;
  onCopied: (created: Array<{ companyId: number; templateId: number; name: string }>) => void;
}> = ({ open, template, currentCompanyId, onClose, onCopied }) => {
  const [companies, setCompanies] = useState<CompanyOption[]>([]);
  const [chosen, setChosen] = useState<number[]>([]);
  const [name, setName] = useState('');
  const [loading, setLoading] = useState(false);
  const [busy, setBusy] = useState(false);
  const [error, setError] = useState<string | null>(null);

  useEffect(() => {
    if (!open) return undefined;
    let cancelled = false;
    setChosen([]);
    setName('');
    setError(null);
    setLoading(true);
    loadMyCompanies()
      .then((list) => { if (!cancelled) setCompanies(list.sort((a, b) => a.name.localeCompare(b.name))); })
      .catch((e: any) => { if (!cancelled) setError(soaErrorMessage(e, 'Your companies could not be loaded')); })
      .finally(() => { if (!cancelled) setLoading(false); });
    return () => { cancelled = true; };
  }, [open]);

  const copy = async () => {
    if (!template) return;
    setBusy(true);
    setError(null);
    try {
      const res: any = await apiService.copySoaTemplate(template.templateId, chosen, name.trim() || undefined);
      onCopied(Array.isArray(res?.data?.created) ? res.data.created : []);
    } catch (e: any) {
      setError(soaErrorMessage(e, 'The template could not be copied'));
    } finally {
      setBusy(false);
    }
  };

  const toggle = (id: number) => setChosen((c) => (c.includes(id) ? c.filter((x) => x !== id) : [...c, id]));

  return (
    <Dialog open={open} onClose={busy ? undefined : onClose} maxWidth="sm" fullWidth>
      <DialogTitle>Copy “{template?.name}” to companies</DialogTitle>
      <DialogContent>
        {error && <Alert severity="error" sx={{ mb: 1 }}>{error}</Alert>}
        <Typography variant="body2" color="text.secondary" sx={{ mb: 1 }}>
          Each company gets its own copy to change freely. A company that already has a template of that name gets “Name (2)”.
          The logo and company details printed are always the company&apos;s own.
        </Typography>
        {loading ? <CircularProgress size={22} /> : (
          <List dense sx={{ maxHeight: 320, overflow: 'auto', border: '1px solid #e3e7ec', borderRadius: 1 }}>
            {companies.map((c) => (
              <ListItem key={c.id} disablePadding sx={{ pl: 1 }}>
                <FormControlLabel
                  sx={{ width: '100%', m: 0 }}
                  control={<Checkbox size="small" checked={chosen.includes(c.id)} onChange={() => toggle(c.id)} />}
                  label={<ListItemText primary={`${c.name}${c.id === currentCompanyId ? ' (this company: a duplicate)' : ''}`} secondary={c.code || undefined} />}
                />
              </ListItem>
            ))}
            {!companies.length && <ListItem><ListItemText primary="No company to copy to." /></ListItem>}
          </List>
        )}
        <Stack direction="row" spacing={1} sx={{ mt: 1.5 }}>
          <Button size="small" onClick={() => setChosen(companies.filter((c) => c.id !== currentCompanyId).map((c) => c.id))} disabled={!companies.length}>All other companies</Button>
          <Button size="small" onClick={() => setChosen([])} disabled={!chosen.length}>None</Button>
        </Stack>
        <TextField sx={{ mt: 2 }} fullWidth size="small" label="Name of the copies (empty = the same name)" value={name}
          onChange={(e) => setName(e.target.value)} inputProps={{ maxLength: 120 }} />
      </DialogContent>
      <DialogActions>
        <Button onClick={onClose} disabled={busy}>Cancel</Button>
        <Button variant="contained" onClick={copy} disabled={busy || !chosen.length} startIcon={busy ? <CircularProgress size={14} /> : undefined}>
          Copy to {chosen.length || ''} compan{chosen.length === 1 ? 'y' : 'ies'}
        </Button>
      </DialogActions>
    </Dialog>
  );
};

// ── versions ─────────────────────────────────────────────────────────────────────────────────

export const VersionsDialog: React.FC<{
  open: boolean; templateId: number; dirty: boolean; onClose: () => void; onRestored: (record: SoaTemplateRecord) => void;
}> = ({ open, templateId, dirty, onClose, onRestored }) => {
  const [versions, setVersions] = useState<SoaTemplateVersion[]>([]);
  const [loading, setLoading] = useState(false);
  const [busy, setBusy] = useState<number | null>(null);
  const [error, setError] = useState<string | null>(null);

  useEffect(() => {
    if (!open) return undefined;
    let cancelled = false;
    setLoading(true);
    setError(null);
    apiService.getSoaTemplateHistory(templateId)
      .then((res: any) => { if (!cancelled) setVersions(Array.isArray(res?.data) ? res.data : []); })
      .catch((e: any) => { if (!cancelled) setError(soaErrorMessage(e, 'The versions could not be loaded')); })
      .finally(() => { if (!cancelled) setLoading(false); });
    return () => { cancelled = true; };
  }, [open, templateId]);

  const restore = async (v: SoaTemplateVersion) => {
    if (dirty && !window.confirm('Your unsaved changes will be lost. Restore this version?')) return;
    setBusy(v.historyId);
    setError(null);
    try {
      const res: any = await apiService.restoreSoaTemplateVersion(templateId, v.historyId);
      onRestored(res.data as SoaTemplateRecord);
    } catch (e: any) {
      setError(soaErrorMessage(e, 'The version could not be restored'));
    } finally {
      setBusy(null);
    }
  };

  return (
    <Dialog open={open} onClose={onClose} maxWidth="sm" fullWidth>
      <DialogTitle>Earlier versions</DialogTitle>
      <DialogContent>
        {error && <Alert severity="error" sx={{ mb: 1 }}>{error}</Alert>}
        <Typography variant="body2" color="text.secondary" sx={{ mb: 1 }}>
          Each save keeps the version it replaced (the last 30). Restoring one keeps the current version here too.
        </Typography>
        {loading ? <CircularProgress size={22} /> : (
          <List dense>
            {versions.map((v) => (
              <ListItem key={v.historyId} secondaryAction={(
                <Button size="small" onClick={() => restore(v)} disabled={busy !== null} startIcon={busy === v.historyId ? <CircularProgress size={14} /> : undefined}>
                  Restore
                </Button>
              )}>
                <ListItemText primary={new Date(v.savedDate).toLocaleString()} secondary={v.savedByName ? `replaced by ${v.savedByName}` : undefined} />
              </ListItem>
            ))}
            {!versions.length && <ListItem><ListItemText primary="No earlier version yet." /></ListItem>}
          </List>
        )}
      </DialogContent>
      <DialogActions><Button onClick={onClose}>Close</Button></DialogActions>
    </Dialog>
  );
};

// ── save as ──────────────────────────────────────────────────────────────────────────────────

export const SaveAsDialog: React.FC<{
  open: boolean; defaultName: string; onClose: () => void; onSave: (name: string) => Promise<void>;
}> = ({ open, defaultName, onClose, onSave }) => {
  const [name, setName] = useState(defaultName);
  const [busy, setBusy] = useState(false);
  const [error, setError] = useState<string | null>(null);
  useEffect(() => { if (open) { setName(defaultName); setError(null); } }, [open, defaultName]);
  return (
    <Dialog open={open} onClose={busy ? undefined : onClose} maxWidth="xs" fullWidth>
      <DialogTitle>Save as a new template</DialogTitle>
      <DialogContent>
        {error && <Alert severity="error" sx={{ mb: 1 }}>{error}</Alert>}
        <TextField sx={{ mt: 1 }} fullWidth label="Name" value={name} autoFocus inputProps={{ maxLength: 120 }} onChange={(e) => setName(e.target.value)} />
      </DialogContent>
      <DialogActions>
        <Button onClick={onClose} disabled={busy}>Cancel</Button>
        <Button
          variant="contained"
          disabled={busy || !name.trim()}
          startIcon={busy ? <CircularProgress size={14} /> : undefined}
          onClick={async () => {
            setBusy(true);
            setError(null);
            try {
              await onSave(name.trim());
            } catch (e: any) {
              setError(soaErrorMessage(e, 'The template could not be saved'));
            } finally {
              setBusy(false);
            }
          }}
        >
          Save
        </Button>
      </DialogActions>
    </Dialog>
  );
};
